import { NgTemplateOutlet } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ComponentRef,
  DestroyRef,
  ViewContainerRef,
  computed,
  inject,
  input,
  viewChild,
} from '@angular/core';

import { DialogService } from '../dialog.service';
import {
  DialogContent,
  DialogInstance,
  DialogSize,
  asDialogContent,
  contentOf,
} from '../dialog.models';

/** Panel max-width per size. Plain geometry - no colour, radius or font token involved. */
const SIZE_MAX_WIDTH: Record<DialogSize, string> = {
  sm: '420px',
  md: '520px',
  lg: '720px',
  xl: '960px',
  full: 'none',
};

/** Minimal subscribable shape shared by output()'s OutputRef and EventEmitter. */
interface OutputLike {
  subscribe(next: (value: never) => void): { unsubscribe(): void };
}

function isOutputLike(value: unknown): value is OutputLike {
  return (
    typeof value === 'object' &&
    value !== null &&
    typeof (value as { subscribe?: unknown }).subscribe === 'function'
  );
}

/**
 * One .modal panel. Owns the header, the dynamically created content and the
 * generic footer.
 *
 * Content is created with ViewContainerRef.createComponent() rather than
 * NgComponentOutlet because the footer must reach the instance to call
 * isFormValid(), getPayload(), setValues() and resetForm() and to subscribe
 * the declared outputs - NgComponentOutlet exposes no handle to what it made.
 *
 * Change detection is Default, deliberately. isFormValid() is a plain method
 * on the content, not a signal, so the footer can only stay in step if the
 * frame's own bindings are re-evaluated on every tick that traverses it. Under
 * OnPush a keystroke inside the content marks only the content dirty and
 * leaves a stale Save button behind.
 */
@Component({
  selector: 'app-dialog-frame',
  imports: [NgTemplateOutlet],
  templateUrl: './dialog-frame.html',
  styleUrl: './dialog-frame.scss',
  host: {
    role: 'dialog',
    tabindex: '-1',
    '[class]': 'panelClasses()',
    '[style]': 'panelStyles()',
    '[attr.aria-modal]': 'isTop() ? "true" : null',
    '[attr.aria-busy]': 'dref().busy() ? "true" : null',
    '[attr.aria-labelledby]': 'hasHeading() ? titleId() : null',
    '[attr.aria-label]': 'hasHeading() ? null : (cfg().ariaLabel ?? null)',
  },
  changeDetection: ChangeDetectionStrategy.Default,
})
export class DialogFrame implements AfterViewInit {
  readonly instance = input.required<DialogInstance>();
  readonly isTop = input.required<boolean>();
  readonly zIndex = input.required<number>();

  private readonly anchor = viewChild.required('content', { read: ViewContainerRef });
  private readonly dialogs = inject(DialogService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly subscriptions: { unsubscribe(): void }[] = [];

  protected readonly cfg = computed(() => this.instance().config);
  protected readonly dref = computed(() => this.instance().ref);
  protected readonly titleId = computed(() => `${this.instance().id}-title`);
  protected readonly hasHeading = computed(
    () => !!(this.dref().title() || this.cfg().titleAccent)
  );

  protected readonly showHeader = computed(
    () => this.hasHeading() || this.cfg().showHeaderClose !== false
  );

  protected readonly showFooter = computed(() => {
    const c = this.cfg();
    return (
      !!c.footerTemplate ||
      c.showSave !== false ||
      c.showCancel !== false ||
      c.showApply === true ||
      c.showReset === true
    );
  });

  protected readonly footerJustify = computed(() => {
    switch (this.cfg().footerAlign) {
      case 'start':
        return 'flex-start';
      case 'between':
        return 'space-between';
      default:
        return 'flex-end';
    }
  });

  protected readonly panelClasses = computed<Record<string, boolean>>(() => {
    const classes: Record<string, boolean> = { modal: true, 'dlg-panel': true };
    const extra = this.cfg().panelClass;
    if (typeof extra === 'string') classes[extra] = true;
    else if (Array.isArray(extra)) for (const name of extra) classes[name] = true;
    return classes;
  });

  protected readonly panelStyles = computed<Record<string, string | null>>(() => {
    const c = this.cfg();
    const offset = c.offset;
    const size: DialogSize = c.size ?? 'md';
    return {
      'z-index': String(this.zIndex()),
      'max-width': c.maxWidth ?? SIZE_MAX_WIDTH[size],
      'max-height': size === 'full' ? '100%' : null,
      width: c.width ?? null,
      height: c.height ?? null,
      'margin-top': offset?.top ?? null,
      'margin-left': offset?.left ?? null,
      'margin-right': offset?.right ?? null,
      'margin-bottom': offset?.bottom ?? null,
    };
  });

  ngAfterViewInit(): void {
    const instance = this.instance();
    const created: ComponentRef<unknown> = this.anchor().createComponent(instance.component, {
      injector: instance.injector,
    });
    instance.ref.contentRef = created;

    const inputs = instance.config.inputs;
    if (inputs) {
      for (const key of Object.keys(inputs)) created.setInput(key, inputs[key]);
    }

    const content = asDialogContent(created.instance);
    if (instance.config.data !== undefined && typeof content?.setValues === 'function') {
      content.setValues(instance.config.data);
    }

    const outputs = instance.config.outputs;
    if (outputs) {
      const bag = created.instance as Record<string, unknown>;
      for (const key of Object.keys(outputs)) {
        const emitter = bag[key];
        if (isOutputLike(emitter)) this.subscriptions.push(emitter.subscribe(outputs[key]));
      }
    }

    // Declared outputs are torn down with the frame, so a closed dialog can
    // never call back into a caller that has already moved on.
    this.destroyRef.onDestroy(() => {
      for (const subscription of this.subscriptions) subscription.unsubscribe();
      this.subscriptions.length = 0;
      created.destroy();
    });

    created.changeDetectorRef.markForCheck();
  }

  /** Header X and footer Cancel both honour the content's canClose() veto. */
  protected onHeaderClose(): void {
    void this.dialogs.requestClose(this.instance(), 'close');
  }

  protected onCancel(): void {
    void this.dialogs.requestClose(this.instance(), 'cancel');
  }

  protected onReset(): void {
    this.content()?.resetForm?.();
  }

  protected onApply(): void {
    this.content()?.onApply?.();
  }

  /**
   * closeOnSave defaults to true: collect the payload and resolve.
   *
   * closeOnSave === false is the keep-open mode: the frame flips busy and hands
   * control to the content's onSave() hook, falling back to onApply() so a
   * component that treats the two identically needs only one hook. The content
   * then owns the round trip - it must close() on success or clear busy on failure.
   */
  protected onSave(): void {
    if (this.saveDisabled()) return;
    const instance = this.instance();
    const content = this.content();
    const payload = typeof content?.getPayload === 'function' ? content.getPayload() : undefined;

    if (instance.config.closeOnSave === false) {
      instance.ref.setBusy(true);
      if (typeof content?.onSave === 'function') content.onSave();
      else content?.onApply?.();
      return;
    }
    instance.ref.close({ action: 'save', data: payload });
  }

  /**
   * disableSaveWhenInvalid defaults to TRUE. A content component that does not
   * implement isFormValid() is never blocked, so display-only dialogs still
   * have a working Save.
   */
  protected saveDisabled(): boolean {
    const instance = this.instance();
    if (instance.ref.busy()) return true;
    if (instance.config.disableSaveWhenInvalid === false) return false;
    const content = this.content();
    return typeof content?.isFormValid === 'function' ? !content.isFormValid() : false;
  }

  protected applyDisabled(): boolean {
    return this.saveDisabled();
  }

  private content(): DialogContent | null {
    return contentOf(this.instance().ref.contentRef);
  }
}
